import React, { useEffect, useMemo, useState } from "react";
import { api, fmtCurrency, STAGE_COLORS } from "../lib/api";
import { PageHeader, VendorTag } from "../components/layout/AppLayout";
import { CalendarBlank } from "@phosphor-icons/react";

export default function Schedule() {
  const [items, setItems] = useState([]);
  useEffect(() => { (async () => setItems((await api.get("/schedule")).data))(); }, []);

  const range = useMemo(() => {
    const dated = items.filter((x) => x.start_date && x.end_date);
    if (dated.length === 0) return null;
    const min = new Date(Math.min(...dated.map((x) => new Date(x.start_date).getTime())));
    const max = new Date(Math.max(...dated.map((x) => new Date(x.end_date).getTime())));
    // Normalize to month starts/ends
    min.setDate(1);
    max.setMonth(max.getMonth() + 1, 0);
    return { min, max };
  }, [items]);

  const months = useMemo(() => {
    if (!range) return [];
    const arr = [];
    const c = new Date(range.min);
    while (c <= range.max) { arr.push(new Date(c)); c.setMonth(c.getMonth() + 1); }
    return arr;
  }, [range]);

  const totalMs = range ? range.max - range.min : 1;

  const barStyle = (item) => {
    if (!item.start_date || !item.end_date) return { display: "none" };
    const s = new Date(item.start_date);
    const e = new Date(item.end_date);
    const left = ((s - range.min) / totalMs) * 100;
    const width = Math.max(((e - s) / totalMs) * 100, 1.5);
    return { left: `${left}%`, width: `${width}%` };
  };

  return (
    <div>
      <PageHeader
        overline="Time Layer"
        title="Delivery Schedule"
        subtitle="Every project on one timeline. Spot overlap, engineer clashes, and quarterly load at a glance."
      />

      <div className="border border-slate-200 bg-white rounded-lg p-6">
        {items.length === 0 && (
          <div className="grid-bg py-20 text-center text-sm text-slate-500 rounded">
            <CalendarBlank size={28} className="mx-auto text-slate-400 mb-3" />
            No projects to schedule.
          </div>
        )}

        {range && (
          <div className="overflow-x-auto">
            <div className="min-w-[880px]">
              {/* Month header */}
              <div className="flex border-b border-slate-200 pb-2 mb-2 ml-[220px]">
                {months.map((m, i) => {
                  const monthMs = new Date(m.getFullYear(), m.getMonth() + 1, 0) - m;
                  const width = (monthMs / totalMs) * 100;
                  return (
                    <div key={i} style={{ width: `${width}%` }} className="text-[10px] font-mono uppercase tracking-wider text-slate-500 border-l border-slate-100 pl-2">
                      {m.toLocaleString("en", { month: "short" })} {m.getFullYear() % 100}
                    </div>
                  );
                })}
              </div>

              {/* Rows */}
              <div className="space-y-2">
                {items.map((it) => (
                  <div key={it.id} data-testid={`schedule-row-${it.id}`} className="flex items-center gap-3">
                    <div className="w-[220px] shrink-0">
                      <div className="text-sm text-[#0F1E38] font-medium truncate">{it.name}</div>
                      <div className="text-[10px] text-slate-500 font-mono truncate">{it.client}</div>
                    </div>
                    <div className="flex-1 relative h-10 bg-slate-50 rounded border border-slate-100">
                      {/* grid lines */}
                      {months.map((m, i) => {
                        const left = ((m - range.min) / totalMs) * 100;
                        return <div key={i} className="absolute top-0 bottom-0 border-l border-slate-100" style={{ left: `${left}%` }} />;
                      })}
                      {it.start_date && it.end_date && (
                        <div
                          className={`absolute top-1 bottom-1 rounded px-2 flex items-center gap-2 border ${STAGE_COLORS[it.stage] || "border-slate-300"} overflow-hidden`}
                          style={{ ...barStyle(it), transition: "left 0.4s ease, width 0.4s ease" }}
                        >
                          <span className="text-[10px] font-mono uppercase truncate">{fmtCurrency(it.value)}</span>
                          <span className="flex gap-1 flex-wrap">{it.vendors?.map(v => <VendorTag key={v} vendor={v} />)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Assigned people */}
      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {items.map((it) => (
          <div key={it.id} className="border border-slate-200 bg-white rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-[#0F1E38]">{it.name}</div>
                <div className="text-[10px] text-slate-500 font-mono uppercase mt-0.5">{it.start_date} → {it.end_date}</div>
              </div>
              <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${STAGE_COLORS[it.stage] || "border-slate-300"}`}>{it.stage}</span>
            </div>
            <div className="mt-3">
              {it.assigned?.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {it.assigned.map((a) => (
                    <span key={a.id} className="text-[11px] font-mono px-2 py-0.5 bg-blue-500/10 text-[#0F1E38] border border-blue-500/30 rounded">{a.name}</span>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-slate-500 font-mono">No assignments yet — assign from Pipeline.</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
